import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { InstrSurgeryIcon, type InstrSurgeryIconName } from '../components/InstrSurgeryIcon';
import './InstrSurgeryPage.css';
import './PortalSectionLayout.css';
import './PortalSectionInteractions.css';
import './HomebleachingPage.css';

// Text transcribed from the supplied Figma screenshots and original PDF.
const sections: { id: string; title: string; icon: InstrSurgeryIconName; lead?: string; items: string[] }[] = [
  { id: 'start', title: 'Wissenswertes vor dem Start', icon: 'info', items: [
    'Nur gesunde Zähne bleichen: Karies, undichte Füllungen, freiliegende Zahnhälse oder Zahnfleischentzündungen müssen vorher behandelt werden.',
    'Eine Zahnreinigung vorab verbessert oft das Ergebnis (Beläge entfernen, gleichmässigere Aufhellung).',
    'Füllungen/Kronen/Veneers werden nicht heller. Es kann zu Farbunterschieden kommen – ggf. muss nach dem Bleaching angepasst werden.',
    'Empfindlichkeit ist möglich (v. a. in den ersten Tagen) und meist vorübergehend.',
  ] },
  { id: 'preparation', title: 'Vorbereitung vor dem Bleaching', icon: 'brush-cleaning', items: [
    'Zähne vorher sanft, aber gründlich reinigen (Zahnbürste/Zahnseide/Interdentalbürsten).',
    'Bei Zahnfleischbluten, Kariesverdacht oder Schmerzen bitte nicht starten → Praxis kontaktieren.',
    'Schiene vor dem Einsetzen trocken (Gel haftet besser).',
  ] },
  { id: 'application', title: 'Anwendung', icon: 'syringe', items: [
    'Pro Zahn nur ein kleiner Tropfen (Reiskorn-Grösse) in die Schiene.',
    'Schiene einsetzen, Überschuss sofort entfernen (Finger/ Wattestäbchen).',
    'Tragezeit exakt nach Empfehlung einhalten (nicht „verlängern“).',
    'Danach die Schiene entfernen und mit kaltem Wasser reinigen',
    'Mund mit Wasser ausspülen, ev. mit einer klaren fluoridhaltigen Mundspüllösungsanft putzen.',
  ] },
  { id: 'after', title: 'Nach dem Bleichen', icon: 'shield-check', lead: 'Für 24–48 h nach jeder Anwendung möglichst vermeiden:', items: [
    'Trinken von Kaffee, schwarzer/grüner Tee, Rotwein, Cola, dunkle Säfte',
    'Essen von farbstoffhaltigen Nahrungsmitteln wie Curry, Tomatensauce, Sojasauce, Beeren',
    'Rauchen/Nikotin',
  ] },
];

export function HomebleachingPage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior }); }, []);
  useEffect(() => {
    const pointer = () => { if (ref.current) ref.current.dataset.input = 'pointer'; };
    const keyboard = () => { if (ref.current) ref.current.dataset.input = 'keyboard'; };
    document.addEventListener('pointerdown', pointer, true);
    document.addEventListener('keydown', keyboard, true);
    return () => {
      document.removeEventListener('pointerdown', pointer, true);
      document.removeEventListener('keydown', keyboard, true);
    };
  }, []);

  return <div ref={ref} className="portal-section-page portal-section-page--advice instr-surgery-page homebleaching-page portal-page-surface">
    <div className="instr-surgery-page__inner portal-page-content">
      <header className="instr-surgery-page__header portal-page-heading">
        <h1>Ratgeber und Tipps</h1><p>Wichtige Verhaltensregeln für ein optimales Bleichresultat.</p>
      </header>
      <section className="portal-section-card instr-surgery-page__hero" aria-labelledby="homebleaching-guide-title">
        <div className="instr-surgery-page__document-tile"><InstrSurgeryIcon name="document" /></div>
        <div className="instr-surgery-page__hero-copy">
          <h2 id="homebleaching-guide-title" className="homebleaching-page__guide-title"
            data-reference-title="Tipps bei chirurgischen Eingriffen"><span>Verhalten bei Homebleaching</span></h2>
          <p>Wichtige Verhaltensregeln für ein optimales Bleichresultat.</p>
        </div>
        <a className="instr-surgery-page__export" href="/patient-documents/Verhalten_Homebleaching_RiviDent.pdf" download>
          <span>Als PDF exportieren</span><InstrSurgeryIcon name="download" />
        </a>
      </section>
      <section className="instr-surgery-page__accordion" aria-label="Verhalten bei Homebleaching">
        {sections.map(section => {
          const open = openId === section.id;
          const triggerId = `homebleaching-${section.id}-trigger`, panelId = `homebleaching-${section.id}-panel`;
          return <article key={section.id} className={`portal-section-card portal-section-card--flush instr-surgery-page__accordion-card${open ? ' instr-surgery-page__accordion-card--open' : ''}`}>
            <button id={triggerId} type="button" className="instr-surgery-page__accordion-trigger" aria-expanded={open} aria-controls={panelId}
              onClick={() => setOpenId(current => current === section.id ? null : section.id)}>
              <span className="instr-surgery-page__section-tile"><InstrSurgeryIcon name={section.icon} /></span>
              <span className="instr-surgery-page__section-title">{section.title}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"
                className={`instr-surgery-page__chevron${open ? ' instr-surgery-page__chevron--open' : ''}`}>
                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {open && <div id={panelId} role="region" aria-labelledby={triggerId} className="instr-surgery-page__accordion-panel">
              {section.lead && <p>{section.lead}</p>}
              <ul>{section.items.map(item => <li key={item}>{item}</li>)}</ul>
            </div>}
          </article>;
        })}
      </section>
      <section className="portal-section-card instr-surgery-page__contact" aria-labelledby="homebleaching-contact-title">
        <div className="instr-surgery-page__support-tile"><InstrSurgeryIcon name="headset" /></div>
        <div className="instr-surgery-page__contact-copy">
          <h3 id="homebleaching-contact-title">Haben Sie weitere Fragen oder akute Beschwerden?</h3>
          <p>Unser Team ist während unseren Öffnungszeiten für Sie erreichbar.</p>
        </div>
        <a className="homebleaching-page__call" href="tel:+41526254625">Praxis anrufen</a>
      </section>
    </div>
  </div>;
}
